import { TrendingUp, FileText, Plus, Sparkles, X, ChevronRight, ArrowLeft, Filter, Search, BellOff } from 'lucide-react';
import type { HierarchyFilter } from './TreeView';
import { useState, useEffect } from 'react';
import { useAlarmReport } from '../contexts/AlarmReportContext';
import { apiFetch } from '../api/client';
import { PointDetail } from './PointDetail';
import { freshnessStatus, CADENCE } from '../lib/freshness';
import { DataUnavailable, FreshnessDot } from './DataState';

const MM_TO_IN = 1 / 25.4;

// Replace any mm/s label with in/s for display
const fmtVelLabel = (s: string) => s.replace(/mm\/s/g, 'in/s');

// Format 'YYYY/MM/DD HH:MM:SS' timestamps (stored as Eastern time) for display.
function fmtEastern(ts: string, showDate = true, showTime = true): string {
  if (!ts) return '';
  const [datePart, timePart = '00:00:00'] = ts.split(' ');
  const [y, m, d] = datePart.split('/').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12  = hh % 12 || 12;
  if (showDate && showTime) return `${months[m-1]} ${d}, ${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
  if (showDate)             return `${months[m-1]} ${d}, ${y}`;
  return `${h12}:${String(mm).padStart(2,'0')} ${ampm}`;
}

interface PointData {
  V: number;
  H: number;
  A: number;
}

interface Thresholds {
  yellow: number;
  red: number;
}

interface Point {
  id: string;
  name: string;
  velocity: PointData;
  acceleration: PointData;
  envelope: PointData;
  temperature: PointData;
  velocityThresholds: Thresholds;
  accelerationThresholds: Thresholds;
  envelopeThresholds: Thresholds;
  temperatureThresholds: Thresholds;
  status: 'low' | 'medium' | 'high';
  sensorSerial?: string;
}

interface Machine {
  id: string;
  name: string;
  health: 'healthy' | 'warning' | 'critical';
  hasNewReport?: boolean;
  imageUrl?: string;
  points: Point[];
  plantName?: string;
  areaName?: string;
}

interface RecentAlarm {
  id: string;
  machine: string;
  area: string;
  plant: string;
  date: string;
  severity: string;
  analysis: string;
  status: 'Open' | 'Closed';
  violatedMetric: string; // e.g., "Velocity", "Temperature", "Acceleration", "Envelope"
  violatedPoint?: string; // e.g., "MDE", "MNDE", "PDE", "PNDE"
  reportId?: string; // Link to associated report if one exists
}

interface AiResult {
  briefing: string;
  // three-section fields (since-login)
  alarm_section?: string;
  report_section?: string;
  roc_section?: string;
  urgent_count?: number;
  urgent_machine_ids?: string[];
  window_label?: string;
  // open-reports
  total_open?: number;
  oldest_report_days?: number;
  // machine-summary
  machine_name?: string;
  alarm_count?: number;
  alarm_pattern?: string;
  has_active_alarms?: boolean;
  // sensor-summary
  total_sensors?: number;
  low_battery_count?: number;
  stale_count?: number;
  generated_at: string;
}

interface ApiMachineListItem {
  id: string;
  name: string;
  area_name: string;
  company_name: string;
  db_id: number;
  db_name: string;
  active_alarms: number;
  last_measured_at: string | null; // from GET /machines — drives freshness dot honesty
}

type CardState = 'menu' | 'date_input' | 'machine_select' | 'db_select_sensor' | 'loading' | 'result' | 'error' | 'dismissed';
type ResultMode = 'since_login' | 'machine' | 'sensor' | 'reports' | null;
type DatePreset = '24h' | '3d' | '1w' | '2w' | '1m' | null;

interface DashboardSummaryProps {
  machines: Machine[];
  onMachineClick: (machineId: string, machineName: string, points: Point[], imageUrl?: string) => void;
  onPointClick?: (machineId: string, machineName: string, pointName: string) => void;
  onViewReport?: (reportId: string) => void;
  onCreateReport?: (alarm: { alarmId: string; machineName: string; metric: string }) => void;
  isAdmin?: boolean;
  hierarchyFilter?: HierarchyFilter | null;
  onClearFilter?: () => void;
}

// API alarm shape from GET /alarms/recent
interface ApiAlarmEvent {
  id: number;
  axis_id: number;
  machine_id: string;
  machine_name: string;
  area_name: string;
  company_name: string;
  db_name: string;
  measurement_at: string;
  unit_label: string;
  unit_code: number;
  point_name: string;
  axis_num: number;
  axis_name: string;
  peak_value: number | null;
  threshold: number | null;
  severity: number;
  resolved: number;
  ei_url?: string;
}

// API rate-of-change shape from GET /alarms/rate-of-change
interface ApiRoC {
  machine_id: string;
  machine_name: string;
  db_name: string;
  area_name: string;
  company_name: string;
  change_pct: number;
  unit_label: string;
}

// Machine health score from GET /machines/health-scores
interface HealthScore {
  machine_id: string;
  health_score: number;
  health_label: string;
  health_colour: string;
  alarm_component: number;
  spectral_component: number;
  roc_component: number;
  raw_score_100: number;
  primary_driver: string | null;
  mitigating_factor: string | null;
  alarm_detail: string | null;
  spectral_detail: string | null;
  roc_detail: string | null;
  alarm_count_90d: number;
  has_waveform_data: boolean;
  has_roc_data: boolean;
}

// Bearing health summary from GET /machines/bearing-health
interface BearingHealthSummary {
  machine_id: string;
  status: string;
  colour: string;
  summary_line: string;
  mitigating: string | null;
  confidence: string;
  has_pv_alarm: boolean;
}

// A group of alarm events for a single machine within a 24-hour window
interface MachineAlarmGroup {
  key: string;           // machine_id + day-bucket
  machine_id: string;
  machine_name: string;
  area_name: string;
  plant: string;
  db_name: string;
  company_name: string;
  events: ApiAlarmEvent[];
  highest_severity: number;
  most_recent_at: string;
  any_open: boolean;
}

interface MappedAlarm extends RecentAlarm {
  _dbName: string;
  _companyName: string;
  _areaName: string;
}

function mapApiAlarm(e: ApiAlarmEvent): MappedAlarm {
  return {
    id: String(e.id),
    machine: e.machine_name,
    area: e.area_name,
    plant: e.company_name || e.db_name,
    date: (e.measurement_at || '').replace(/\//g, '-').replace(' ', 'T'),
    severity: String(e.severity),
    analysis: '',
    status: e.resolved ? 'Closed' : 'Open',
    violatedMetric: e.unit_label,
    violatedPoint: e.point_name,
    _dbName: e.db_name,
    _companyName: e.company_name,
    _areaName: e.area_name,
  };
}

function groupAlarmsByMachine(events: ApiAlarmEvent[]): MachineAlarmGroup[] {
  const map = new Map<string, MachineAlarmGroup>();

  for (const e of events) {
    // Parse timestamp 'YYYY/MM/DD HH:MM:SS' → day bucket (UTC)
    const ts = new Date((e.measurement_at || '').replace(/\//g, '-').replace(' ', 'T'));
    const dayBucket = isNaN(ts.getTime()) ? '0' : String(Math.floor(ts.getTime() / 86400000));
    const key = `${e.machine_id}__${dayBucket}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        machine_id: e.machine_id,
        machine_name: e.machine_name,
        area_name: e.area_name,
        plant: e.company_name || e.db_name,
        db_name: e.db_name,
        company_name: e.company_name,
        events: [],
        highest_severity: 0,
        most_recent_at: e.measurement_at,
        any_open: false,
      });
    }

    const group = map.get(key)!;
    group.events.push(e);
    if (e.severity > group.highest_severity) group.highest_severity = e.severity;
    if (!e.resolved) group.any_open = true;
    // Keep most recent timestamp
    if (e.measurement_at > group.most_recent_at) group.most_recent_at = e.measurement_at;
  }

  // Sort by most recent descending
  return Array.from(map.values()).sort((a, b) =>
    b.most_recent_at.localeCompare(a.most_recent_at)
  );
}

interface MachineRocRow {
  axis_id: number;
  point_name: string;
  axis_num: number;
  unit_label: string;
  change_pct: number;
  recent_avg: number;
  prev_avg: number;
  slope: number | null;
}

export function DashboardSummary({ machines, onMachineClick, onPointClick, onViewReport, onCreateReport, isAdmin = false, hierarchyFilter, onClearFilter }: DashboardSummaryProps) {
  const { getReportIdForAlarm } = useAlarmReport();

  const [recentAlarms, setRecentAlarms] = useState<ApiAlarmEvent[]>([]);
  const [alarmsLoading, setAlarmsLoading] = useState(true);
  const [totalActiveAlarms, setTotalActiveAlarms] = useState<number | null>(null);
  const [visibleGroups, setVisibleGroups] = useState(12);

  // W4 (F-07/F-08): per-panel FETCH-FAILURE flags. A failed fetch must render a
  // VISIBLE error, never fall through to an empty/healthy default. These are set
  // true ONLY in .catch (a genuine failure), and reset false when a fetch starts.
  const [recentError, setRecentError] = useState(false);
  const [summaryError, setSummaryError] = useState(false);
  const [rocError, setRocError] = useState(false);
  const [healthScoresError, setHealthScoresError] = useState(false);
  const [bearingHealthError, setBearingHealthError] = useState(false);

  // Alarm detail modal
  const [selectedGroup, setSelectedGroup] = useState<MachineAlarmGroup | null>(null);
  const [groupRoc, setGroupRoc] = useState<MachineRocRow[]>([]);
  const [rocModalLoading, setRocModalLoading] = useState(false);


  const [rocMachines, setRocMachines] = useState<Array<{
    id: string; name: string; health: 'healthy' | 'warning' | 'critical';
    rateOfChange: number; unitLabel: string; plantName: string; areaName: string; points: Point[]; imageUrl?: string;
    _dbName: string; _companyName: string; _areaName: string;
  }>>([]);
  const [rocLoading, setRocLoading] = useState(true);

  const [healthScores, setHealthScores] = useState<Map<string, HealthScore>>(new Map());
  const [bearingHealth, setBearingHealth] = useState<Map<string, BearingHealthSummary>>(new Map());
  const [openAiCardId, setOpenAiCardId] = useState<string | null>(null);
  const [aiCardLoading, setAiCardLoading] = useState(false);
  const [aiCardResults, setAiCardResults] = useState<Map<string, string>>(new Map());

  const [alarmHours, setAlarmHours] = useState(24);

  // Silence alarm state
  const [silencingGroupKey, setSilencingGroupKey] = useState<string | null>(null);
  const [silencing, setSilencing] = useState(false);
  useEffect(() => {
    if (!silencingGroupKey) return;
    const handler = (e: MouseEvent) => {
      const el = document.getElementById('silence-picker');
      if (el && !el.contains(e.target as Node)) setSilencingGroupKey(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [silencingGroupKey]);

  const refreshAlarms = () => {
    setAlarmsLoading(true);
    setRecentError(false);
    apiFetch<ApiAlarmEvent[]>(`/alarms/recent?hours=${alarmHours}`)
      .then(data => { setRecentAlarms(data); setRecentError(false); })
      .catch(() => { setRecentAlarms([]); setRecentError(true); })
      .finally(() => setAlarmsLoading(false));
  };

  const handleSilenceConfirm = (group: MachineAlarmGroup, days: number) => {
    setSilencing(true);
    // Collect unique (axis_id, unit_code) pairs from this group's events
    const seen = new Set<string>();
    const silences: { axis_id: number; unit_code: number }[] = [];
    for (const e of group.events) {
      const key = `${e.axis_id}:${e.unit_code}`;
      if (!seen.has(key)) { seen.add(key); silences.push({ axis_id: e.axis_id, unit_code: e.unit_code }); }
    }
    apiFetch('/alarms/silence', { method: 'POST', body: JSON.stringify({ silences, days }) })
      .then(() => {
        setSilencingGroupKey(null);
        refreshAlarms();
      })
      .catch(() => {})
      .finally(() => setSilencing(false));
  };

  // Fetch recent alarms when time window changes
  useEffect(() => {
    setAlarmsLoading(true);
    setVisibleGroups(12);
    setRecentError(false);
    apiFetch<ApiAlarmEvent[]>(`/alarms/recent?hours=${alarmHours}`)
      .then(data => { setRecentAlarms(data); setRecentError(false); })
      .catch(() => { setRecentAlarms([]); setRecentError(true); })
      .finally(() => setAlarmsLoading(false));
  }, [alarmHours]);

  // Fetch total active count once
  useEffect(() => {
    apiFetch<{ total_baselines: number }>('/alarms/summary')
      .then(data => { setTotalActiveAlarms(data.total_baselines); setSummaryError(false); })
      .catch(() => setSummaryError(true));
  }, []);

  // Fetch rate-of-change once on mount (fixed 7-day recent vs 45-day prior window)
  useEffect(() => {
    setRocLoading(true);
    setRocError(false);
    apiFetch<ApiRoC[]>('/alarms/rate-of-change?limit=5')
      .then(data => {
        const seen = new Map<string, ApiRoC>();
        for (const r of data) {
          const existing = seen.get(r.machine_id);
          if (!existing || Math.abs(r.change_pct) > Math.abs(existing.change_pct)) {
            seen.set(r.machine_id, r);
          }
        }
        setRocMachines(
          Array.from(seen.values())
            .sort((a, b) => Math.abs(b.change_pct) - Math.abs(a.change_pct))
            .slice(0, 5)
            .map(r => ({
              id: r.machine_id,
              name: r.machine_name,
              // F-02/F-11: dot severity is DERIVED from the real change_pct
              // magnitude (never a constant), and the sign is preserved.
              health: (Math.abs(r.change_pct) >= 25 ? 'critical'
                     : Math.abs(r.change_pct) >= 10 ? 'warning'
                     : 'healthy') as 'healthy' | 'warning' | 'critical',
              rateOfChange: Math.round(r.change_pct),
              unitLabel: r.unit_label,
              plantName: r.company_name || r.db_name,
              areaName: r.area_name,
              points: [],
              _dbName: r.db_name,
              _companyName: r.company_name,
              _areaName: r.area_name,
            }))
        );
      })
      .catch(() => { setRocMachines([]); setRocError(true); })
      .finally(() => setRocLoading(false));
  }, []);

  // Fetch health scores once on mount
  useEffect(() => {
    apiFetch<HealthScore[]>('/machines/health-scores')
      .then(data => {
        const map = new Map<string, HealthScore>();
        for (const h of data) map.set(h.machine_id, h);
        setHealthScores(map);
        setHealthScoresError(false);
      })
      .catch(() => setHealthScoresError(true));
  }, []);

  // Fetch bearing health once on mount
  useEffect(() => {
    apiFetch<BearingHealthSummary[]>('/machines/bearing-health')
      .then(data => {
        const map = new Map<string, BearingHealthSummary>();
        for (const b of data) map.set(b.machine_id, b);
        setBearingHealth(map);
        setBearingHealthError(false);
      })
      .catch(() => setBearingHealthError(true));
  }, []);

  // -------------------------------------------------------------------------
  // AI Assistant state
  // -------------------------------------------------------------------------
  const AI_DISMISSED_KEY = 'pc_ai_dismissed';
  const [aiState, setAiState] = useState<CardState>(() =>
    sessionStorage.getItem(AI_DISMISSED_KEY) ? 'dismissed' : 'menu'
  );
  const [aiResult, setAiResult]           = useState<AiResult | null>(null);
  const [aiError, setAiError]             = useState<string | null>(null);
  const [aiContextLabel, setAiContextLabel] = useState('');
  const [aiResultMode, setAiResultMode]   = useState<ResultMode>(null);
  // F-13: remember the last AI request so "Try again" can genuinely re-invoke it.
  const [lastAiCall, setLastAiCall] = useState<
    { endpoint: string; body: object | null; contextLabel: string; mode: ResultMode } | null
  >(null);

  // Reusable since ISO string — set by date_input, reused by open_reports
  const [selectedSince, setSelectedSince] = useState<string | null>(null);

  // Date input sub-state
  const [datePreset, setDatePreset]   = useState<DatePreset>(null);
  const [customDate, setCustomDate]   = useState('');

  // Machine / scope select sub-state
  const [machineList, setMachineList]         = useState<ApiMachineListItem[]>([]);
  const [machineListLoaded, setMachineListLoaded] = useState(false);
  const [selectedDbId, setSelectedDbId]       = useState<number | null>(null);
  const [selectedArea, setSelectedArea]       = useState<string | null>(null);

  // Option 1 & 3 database filter (null = all)
  const [option1DbId, setOption1DbId]         = useState<number | null>(null);
  const [option3DbId, setOption3DbId]         = useState<number | null>(null);

  // Machine search
  const [searchQuery, setSearchQuery]     = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const searchResults = searchQuery.trim().length > 0
    ? machineList.filter(m =>
        m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.area_name.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8)
    : [];

  // Load machine list once on mount (for machine-summary option)
  useEffect(() => {
    if (machineListLoaded) return;
    apiFetch<ApiMachineListItem[]>('/machines')
      .then(data => { setMachineList(data); setMachineListLoaded(true); })
      .catch(() => setMachineListLoaded(true));
  }, [machineListLoaded]);

  // Derived hierarchy — keyed by db_id
  const databases = Array.from(
    new Map(machineList.map(m => [m.db_id, { db_id: m.db_id, db_name: m.db_name }])).values()
  ).sort((a, b) => a.db_name.localeCompare(b.db_name));

  const areasForDb = selectedDbId !== null
    ? Array.from(new Set(machineList.filter(m => m.db_id === selectedDbId).map(m => m.area_name))).sort()
    : [];
  const machinesForArea = (selectedDbId !== null && selectedArea)
    ? machineList.filter(m => m.db_id === selectedDbId && m.area_name === selectedArea)
    : [];

  const dismissAi = () => {
    sessionStorage.setItem(AI_DISMISSED_KEY, '1');
    setAiState('dismissed');
  };

  const resetToMenu = () => {
    setAiResult(null);
    setAiError(null);
    setAiResultMode(null);
    setDatePreset(null);
    setCustomDate('');
    setSelectedDbId(null);
    setSelectedArea(null);
    setOption1DbId(null);
    setOption3DbId(null);
    setAiState('menu');
  };

  const runAiCall = (endpoint: string, body: object | null, contextLabel: string, mode: ResultMode) => {
    setLastAiCall({ endpoint, body, contextLabel, mode });
    setAiContextLabel(contextLabel);
    setAiResultMode(mode);
    setAiState('loading');
    setAiError(null);
    apiFetch<AiResult>(endpoint, { method: 'POST', body: body ? JSON.stringify(body) : '{}' })
      .then(data => { setAiResult(data); setAiState('result'); })
      .catch(err => { setAiError(err?.message || 'Request failed'); setAiState('error'); });
  };

  const handleOption1Generate = () => {
    let since: string;
    const dbScope = option1DbId !== null ? { db_id: option1DbId } : {};
    const dbLabel = option1DbId !== null ? ` — ${databases.find(d => d.db_id === option1DbId)?.db_name ?? ''}` : '';
    if (datePreset) {
      const now = new Date();
      const map: Record<string, number> = { '24h': 1, '3d': 3, '1w': 7, '2w': 14, '1m': 30 };
      const days = map[datePreset] ?? 7;
      since = new Date(now.getTime() - days * 86400000).toISOString();
      const label = datePreset === '24h' ? 'last 24h (rolling, UTC)' : datePreset === '3d' ? 'last 3 days' :
        datePreset === '1w' ? 'last week' : datePreset === '2w' ? 'last 2 weeks' : 'last month';
      setSelectedSince(since);
      runAiCall('/ai/since-login', { since, ...dbScope }, `Since ${label}${dbLabel}`, 'since_login');
    } else if (customDate) {
      since = new Date(customDate).toISOString();
      setSelectedSince(since);
      runAiCall('/ai/since-login', { since, ...dbScope }, `Since ${customDate}${dbLabel}`, 'since_login');
    }
  };

  const handleMachineSelect = (m: ApiMachineListItem) =>
    runAiCall('/ai/machine-summary', { machine_id: m.id }, `Machine summary: ${m.name}`, 'machine');

  // Scope summary: db-level or area-level → calls since-login with 30-day window
  const handleScopeSelect = (dbId: number, areaName?: string) => {
    const since30d = new Date(Date.now() - 30 * 86400000).toISOString();
    const db = databases.find(d => d.db_id === dbId);
    const label = areaName ? `${db?.db_name} — ${areaName}` : (db?.db_name ?? 'Database');
    const body: Record<string, unknown> = { since: since30d, db_id: dbId };
    if (areaName) body.area_name = areaName;
    runAiCall('/ai/since-login', body, `30-day summary: ${label}`, 'since_login');
  };

  const handleSensorSummary = (dbId: number | null) => {
    const db = dbId !== null ? databases.find(d => d.db_id === dbId) : null;
    const label = db ? `Sensor health: ${db.db_name}` : 'Sensor health summary';
    const body = dbId !== null ? { db_id: dbId } : null;
    runAiCall('/ai/sensor-summary', body, label, 'sensor');
  };

  const handleOpenReports = () =>
    runAiCall('/ai/open-reports', selectedSince ? { since: selectedSince } : null, 'Open reports', 'reports');

  const presetLabel: Record<string, string> = { '24h': '24 hrs', '3d': '3 Days', '1w': '1 Week', '2w': '2 Weeks', '1m': '1 Month' };
  const canGenerate = !!datePreset || !!customDate;

  const todayStr   = new Date().toISOString().substring(0, 10);
  const minDateStr = new Date(Date.now() - 365 * 86400000).toISOString().substring(0, 10);

  // Apply hierarchy filter
  const matchFilter = (dbName: string, companyName: string, areaName: string) => {
    if (!hierarchyFilter) return true;
    if (dbName !== hierarchyFilter.dbName) return false;
    if (hierarchyFilter.companyName && companyName !== hierarchyFilter.companyName) return false;
    if (hierarchyFilter.areaName && areaName !== hierarchyFilter.areaName) return false;
    return true;
  };

  const filteredAlarms = recentAlarms.filter(a => matchFilter(a.db_name, a.company_name, a.area_name));
  const alarmGroups = groupAlarmsByMachine(filteredAlarms);
  const machinesWithRoC = rocMachines.filter(m => matchFilter(m._dbName, m._companyName, m._areaName));

  const getHealthColor = (health: 'healthy' | 'warning' | 'critical') => {
    switch (health) {
      case 'healthy':
        return 'bg-green-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'critical':
        return 'bg-red-500';
    }
  };

  const getHealthTextColor = (health: 'healthy' | 'warning' | 'critical') => {
    switch (health) {
      case 'healthy':
        return 'text-green-600';
      case 'warning':
        return 'text-yellow-600';
      case 'critical':
        return 'text-red-600';
    }
  };

  const getSeverityColor = (severity: string) => {
    const sev = parseInt(severity);
    if (sev >= 3) return 'text-red-600';
    if (sev >= 2) return 'text-yellow-600';
    return 'text-gray-600';
  };

  const handleViewReport = (reportId: string) => {
    if (onViewReport) {
      onViewReport(reportId);
    }
  };

  return (
    <div className="space-y-6">

      {/* AI Assistant Card */}
      {aiState !== 'dismissed' && (
        <div className="bg-gradient-to-br from-white to-cyan-50 rounded-xl border border-cyan-100 shadow-sm overflow-hidden">
          {/* Card header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-cyan-100">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-cyan-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-cyan-600" />
              </div>
              <span className="text-sm text-gray-600">AI Assistant</span>
            </div>
            {aiState !== 'loading' && (
              <button onClick={dismissAi} className="text-gray-400 hover:text-gray-600 transition-colors" title="Dismiss">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* STATE: menu */}
          {aiState === 'menu' && (
            <div className="divide-y divide-gray-100">
              {[
                { n: 1, label: 'Alarm briefing — by time period & database',      action: () => setAiState('date_input') },
                { n: 2, label: 'Summarize a database, area, or machine',           action: () => setAiState('machine_select') },
                { n: 3, label: 'Sensor health summary — by database',              action: () => setAiState('db_select_sensor') },
                { n: 4, label: 'Open reports summary',                             action: handleOpenReports },
              ].map(opt => (
                <button key={opt.n} onClick={opt.action}
                  className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50 transition-colors text-left">
                  <span className="text-sm text-gray-700">
                    <span className="text-gray-400 mr-2.5">{opt.n}.</span>{opt.label}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* STATE: date_input */}
          {aiState === 'date_input' && (
            <div className="px-5 py-4 space-y-4">
              <button onClick={() => setAiState('menu')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <div>
                <p className="text-xs text-gray-500 mb-1.5">Database (optional):</p>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => setOption1DbId(null)}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${option1DbId === null ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                    All
                  </button>
                  {databases.map(db => (
                    <button key={db.db_id} onClick={() => setOption1DbId(db.db_id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${option1DbId === db.db_id ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                      {db.db_name}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-sm text-gray-700">Show me what happened since:</p>
              <div className="flex flex-wrap gap-2">
                {(['24h','3d','1w','2w','1m'] as DatePreset[]).filter(Boolean).map(p => (
                  <button key={p!} onClick={() => { setDatePreset(p); setCustomDate(''); }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      datePreset === p ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                    }`}>
                    {presetLabel[p!]}
                  </button>
                ))}
              </div>
              <div>
                <input type="date" value={customDate} min={minDateStr} max={todayStr}
                  onChange={e => { setCustomDate(e.target.value); setDatePreset(null); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-cyan-500" />
              </div>
              <button onClick={handleOption1Generate} disabled={!canGenerate}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                Generate Briefing →
              </button>
            </div>
          )}

          {/* STATE: machine_select */}
          {aiState === 'machine_select' && (
            <div className="px-5 py-4 space-y-3">
              <button onClick={() => setAiState('menu')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>

              {/* Breadcrumb */}
              {(selectedDbId !== null || selectedArea) && (
                <div className="flex items-center gap-1 text-xs text-gray-500 flex-wrap">
                  <button onClick={() => { setSelectedDbId(null); setSelectedArea(null); }}
                    className="hover:text-cyan-600 transition-colors">Databases</button>
                  {selectedDbId !== null && (
                    <>
                      <ChevronRight className="w-3 h-3 flex-shrink-0" />
                      <button onClick={() => setSelectedArea(null)} className="hover:text-cyan-600 transition-colors">
                        {databases.find(d => d.db_id === selectedDbId)?.db_name ?? selectedDbId}
                      </button>
                    </>
                  )}
                  {selectedArea && (
                    <>
                      <ChevronRight className="w-3 h-3 flex-shrink-0" />
                      <span className="text-gray-700">{selectedArea}</span>
                    </>
                  )}
                </div>
              )}

              <p className="text-sm text-gray-700">
                {selectedDbId === null ? 'Select a database or summarize:' : !selectedArea ? 'Select an area or summarize:' : `Machines in ${selectedArea}:`}
              </p>

              <div className="overflow-y-auto max-h-60 divide-y divide-gray-100 border border-gray-200 rounded-lg">
                {!machineListLoaded ? (
                  <div className="p-3 text-sm text-gray-400">Loading…</div>
                ) : selectedDbId === null ? (
                  // Level 0: Databases
                  databases.map(db => {
                    const alarmCount = machineList.filter(m => m.db_id === db.db_id && m.active_alarms > 0).length;
                    return (
                      <div key={db.db_id} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          {alarmCount > 0 && <span className="text-xs text-red-500 shrink-0">{alarmCount} alarm{alarmCount !== 1 ? 's' : ''}</span>}
                          <span className="text-sm text-gray-800 truncate">{db.db_name}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <button onClick={() => handleScopeSelect(db.db_id)}
                            className="text-xs text-cyan-600 hover:text-cyan-800 font-medium transition-colors">
                            Summarize
                          </button>
                          <button onClick={() => setSelectedDbId(db.db_id)}
                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-0.5">
                            Browse <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : !selectedArea ? (
                  // Level 1: Areas
                  areasForDb.map(area => {
                    const alarmCount = machineList.filter(m => m.db_id === selectedDbId && m.area_name === area && m.active_alarms > 0).length;
                    return (
                      <div key={area} className="flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center gap-2 min-w-0">
                          {alarmCount > 0 && <span className="text-xs text-red-500 shrink-0">{alarmCount} alarm{alarmCount !== 1 ? 's' : ''}</span>}
                          <span className="text-sm text-gray-800 truncate">{area}</span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0 ml-2">
                          <button onClick={() => handleScopeSelect(selectedDbId!, area)}
                            className="text-xs text-cyan-600 hover:text-cyan-800 font-medium transition-colors">
                            Summarize
                          </button>
                          <button onClick={() => setSelectedArea(area)}
                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-0.5">
                            Browse <ChevronRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                ) : machinesForArea.length === 0 ? (
                  <div className="p-3 text-sm text-gray-400">No machines found</div>
                ) : (
                  // Level 2: Machines
                  machinesForArea.map(m => (
                    <button key={m.id} onClick={() => handleMachineSelect(m)}
                      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors text-left">
                      <div className="flex items-center gap-2 min-w-0">
                        <FreshnessDot
                          healthColor={m.active_alarms > 0 ? '#ef4444' : '#22c55e'}
                          freshness={freshnessStatus(m.last_measured_at, { cadenceMs: CADENCE.hourlyPoll, staleMult: 24, disconnectedMult: 120 })}
                          size={10}
                        />
                        <span className="text-sm text-gray-800 truncate">{m.name}</span>
                      </div>
                      {m.active_alarms > 0 && (
                        <span className="text-xs text-red-500 ml-2 flex-shrink-0">{m.active_alarms} alarm{m.active_alarms !== 1 ? 's' : ''}</span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          {/* STATE: db_select_sensor */}
          {aiState === 'db_select_sensor' && (
            <div className="px-5 py-4 space-y-3">
              <button onClick={() => setAiState('menu')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <p className="text-sm text-gray-700">Select database for sensor health:</p>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => handleSensorSummary(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${option3DbId === null ? 'bg-cyan-600 text-white border-cyan-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                  All Databases
                </button>
                {databases.map(db => (
                  <button key={db.db_id} onClick={() => handleSensorSummary(db.db_id)}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border bg-white text-gray-600 border-gray-200 hover:border-cyan-400 transition-colors">
                    {db.db_name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STATE: loading */}
          {aiState === 'loading' && (
            <div className="px-5 py-6 space-y-3">
              <div className="space-y-2">
                <div className="h-3.5 bg-cyan-100 rounded animate-pulse w-3/4" />
                <div className="h-3.5 bg-cyan-100 rounded animate-pulse w-full" />
                <div className="h-3.5 bg-cyan-100 rounded animate-pulse w-5/6" />
                <div className="h-3.5 bg-cyan-100 rounded animate-pulse w-2/3" />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Sparkles className="w-4 h-4 text-cyan-500 animate-pulse" />
                <span className="text-sm text-gray-400">Generating your briefing…</span>
              </div>
            </div>
          )}

          {/* STATE: result */}
          {aiState === 'result' && aiResult && (
            <div className="px-5 py-4 space-y-3">
              <p className="text-xs text-gray-400">{aiContextLabel}</p>

              {/* Three-section layout for alarm briefing */}
              {aiResultMode === 'since_login' && aiResult.alarm_section ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-cyan-600 uppercase tracking-wide mb-1">Alarm Summary</p>
                    <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{aiResult.alarm_section}</p>
                  </div>
                  {aiResult.report_section && aiResult.report_section !== 'No open reports.' && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs font-semibold text-cyan-600 uppercase tracking-wide mb-1">Report Summary</p>
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{aiResult.report_section}</p>
                    </div>
                  )}
                  {aiResult.roc_section && aiResult.roc_section !== 'No significant trends detected.' && (
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-xs font-semibold text-cyan-600 uppercase tracking-wide mb-1">Rate of Change</p>
                      <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{aiResult.roc_section}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{aiResult.briefing}</p>
              )}

              {(aiResult.urgent_count ?? 0) > 0 && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                  {aiResult.urgent_count} item{aiResult.urgent_count !== 1 ? 's' : ''} need attention
                </span>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                <button onClick={resetToMenu} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
                  <ArrowLeft className="w-3.5 h-3.5" /> Ask something else
                </button>
                <button onClick={dismissAi} className="text-sm text-gray-400 hover:text-gray-600 transition-colors">Dismiss</button>
              </div>
            </div>
          )}

          {/* STATE: error */}
          {aiState === 'error' && (
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-gray-600">Unable to generate briefing right now.</p>
              {aiError && <p className="text-xs text-gray-400">{aiError}</p>}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (lastAiCall) {
                      runAiCall(lastAiCall.endpoint, lastAiCall.body, lastAiCall.contextLabel, lastAiCall.mode);
                    } else {
                      setAiState('menu');
                    }
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700 transition-colors">Try again</button>
                <button onClick={resetToMenu} className="text-sm text-gray-500 hover:text-gray-700 transition-colors">Ask something else</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm flex items-center justify-between gap-4">
        <div className="flex-1 min-w-0">
          {hierarchyFilter ? (
            <div className="flex items-center gap-2 mt-2">
              <Filter className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-sm text-blue-600 font-medium">{hierarchyFilter.label}</span>
              {onClearFilter && (
                <button onClick={onClearFilter} className="text-xs text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-0.5">
                  <X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          ) : (
            <p className="text-gray-600 mt-2">Real-time monitoring summary of all industrial machines</p>
          )}

          {/* Machine search */}
          <div className="relative mt-4 max-w-sm">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search machines…"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 bg-white focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-200 transition-colors"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 transition-colors">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Dropdown results */}
            {searchFocused && searchResults.length > 0 && (
              <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                {searchResults.map(m => (
                  <button
                    key={m.id}
                    onMouseDown={() => {
                      onMachineClick(m.id, m.name, []);
                      setSearchQuery('');
                      setSearchFocused(false);
                    }}
                    className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FreshnessDot
                        healthColor={m.active_alarms > 0 ? '#ef4444' : '#22c55e'}
                        freshness={freshnessStatus(m.last_measured_at, { cadenceMs: CADENCE.hourlyPoll, staleMult: 24, disconnectedMult: 120 })}
                        size={10}
                      />
                      <span className="text-sm text-gray-800 truncate">{m.name}</span>
                    </div>
                    <span className="text-xs text-gray-400 ml-2 flex-shrink-0">{m.area_name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* No results */}
            {searchFocused && searchQuery.trim().length > 0 && searchResults.length === 0 && (
              <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2.5">
                <span className="text-sm text-gray-400">No machines found</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {aiState === 'dismissed' && (
            <button
              onClick={() => {
                sessionStorage.removeItem(AI_DISMISSED_KEY);
                setAiState('menu');
              }}
              className="flex items-center gap-1.5 px-3 py-2 bg-cyan-50 border border-cyan-200 text-cyan-600 rounded-lg hover:bg-cyan-100 transition-colors text-sm"
              title="Open AI Assistant"
            >
              <Sparkles className="w-3.5 h-3.5" />
              AI Assistant
            </button>
          )}
          {summaryError ? (
            // W4: /alarms/summary failed — show a visible unavailable state, not a
            // bland '—' that reads like "zero / loading".
            <div className="min-w-[120px]">
              <DataUnavailable compact message="Threshold count unavailable" />
            </div>
          ) : (
            <div className="flex flex-col items-center bg-red-50 border border-red-200 rounded-xl px-6 py-3 min-w-[120px]">
              <span className="text-3xl font-bold text-red-600">
                {totalActiveAlarms === null ? '—' : totalActiveAlarms}
              </span>
              <span className="text-sm text-red-500 mt-1 whitespace-nowrap">Thresholds</span>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="space-y-6">
        {/* Recent Alarms */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <FileText className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-gray-900">Recent Alarms</h3>
                <p className="text-gray-600">
                  {alarmHours === 24 ? 'Last 24h (rolling, UTC)' : alarmHours === 72 ? 'Last 3 days' : alarmHours === 168 ? 'Last 7 days' : alarmHours === 336 ? 'Last 2 weeks' : 'Last 30 days'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {([
                { label: '24h',    hours: 24  },
                { label: '3 days', hours: 72  },
                { label: '1 week', hours: 168 },
                { label: '2 weeks',hours: 336 },
                { label: '1 month',hours: 720 },
              ]).map(opt => (
                <button
                  key={opt.hours}
                  onClick={() => setAlarmHours(opt.hours)}
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                    alarmHours === opt.hours
                      ? 'bg-red-500 text-white border-red-500'
                      : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* W4: machine-health enrichment fetch failures (health-scores decorate
              these cards; bearing-health is machine-health context). Surfaced so a
              silently-missing badge is never mistaken for "healthy / no issue". */}
          {(healthScoresError || bearingHealthError) && (
            <div className="mb-3">
              <DataUnavailable
                compact
                message={
                  healthScoresError && bearingHealthError
                    ? 'Machine health scores and bearing health could not be loaded'
                    : healthScoresError
                    ? 'Machine health scores could not be loaded'
                    : 'Bearing health could not be loaded'
                }
              />
            </div>
          )}

          {alarmsLoading ? (
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-20 bg-gray-100 rounded-lg animate-pulse" />)}
            </div>
          ) : recentError ? (
            <DataUnavailable compact message="Recent alarms could not be loaded" onRetry={refreshAlarms} />
          ) : alarmGroups.length === 0 ? (
            <div className="text-gray-500 py-8 text-center">
              <p>No recent alarms</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {alarmGroups.slice(0, visibleGroups).map((group) => {
                  const ts = group.most_recent_at || '';
                  const sevColor = group.highest_severity >= 3 ? 'text-red-600' : group.highest_severity >= 2 ? 'text-yellow-600' : 'text-gray-500';
                  const dotColor = group.highest_severity >= 3 ? 'bg-red-500' : group.highest_severity >= 2 ? 'bg-yellow-400' : 'bg-gray-400';
                  const varLabels = Array.from(new Set(group.events.map(e => fmtVelLabel(e.unit_label)))).slice(0, 2);
                  const hs = healthScores.get(group.machine_id);
                  const openModal = () => {
                    setSelectedGroup(group);
                    setGroupRoc([]);
                    setRocModalLoading(true);
                    apiFetch<{ rate_of_change: MachineRocRow[] }>(`/machines/${group.machine_id}`)
                      .then(d => setGroupRoc(d.rate_of_change || []))
                      .catch(() => setGroupRoc([]))
                      .finally(() => setRocModalLoading(false));
                  };
                  const handleAiClick = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    const mid = group.machine_id;
                    if (openAiCardId === mid) { setOpenAiCardId(null); return; }
                    setOpenAiCardId(mid);
                    if (!aiCardResults.has(mid)) {
                      setAiCardLoading(true);
                      apiFetch<{ briefing: string }>('/ai/machine-summary', {
                        method: 'POST',
                        body: JSON.stringify({ machine_id: mid }),
                      })
                        .then(res => setAiCardResults(prev => new Map(prev).set(mid, res.briefing)))
                        .catch(() => setAiCardResults(prev => new Map(prev).set(mid, 'Unable to load AI context.')))
                        .finally(() => setAiCardLoading(false));
                    }
                  };
                  return (
                    <div
                      key={group.key}
                      onClick={openModal}
                      className="bg-white rounded-lg py-2.5 px-3 border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all flex flex-col gap-1 text-left cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-1">
                        <div className="flex items-center gap-1.5 min-w-0">
                          {hs && (
                            <span
                              className="text-xs font-bold text-white rounded px-1 py-0.5 leading-none shrink-0"
                              style={{ backgroundColor: hs.health_colour }}
                            >
                              {hs.health_score}
                            </span>
                          )}
                          <span className="text-xs font-semibold text-gray-900 leading-tight truncate">{group.machine_name}</span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <div className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                          <span className={`text-xs ${sevColor}`}>{group.events.length > 1 ? `${group.events.length}` : '1'}</span>
                        </div>
                      </div>
                      <div className={`text-xs leading-tight ${sevColor}`}>
                        {varLabels.join(', ')}{group.events.length > varLabels.length ? ` +${group.events.length - varLabels.length}` : ''}
                      </div>
                      {hs?.primary_driver && (
                        <div className="text-xs text-gray-500 leading-tight truncate">{hs.primary_driver}</div>
                      )}
                      <div className="text-xs text-gray-400 truncate">{group.plant} → {group.area_name}</div>
                      <div className="flex items-center justify-between gap-1">
                        <div className="text-xs text-gray-400">{fmtEastern(ts) || group.most_recent_at}</div>
                        <div className="flex items-center gap-1 shrink-0">
                          {isAdmin && (
                            <div className="relative">
                              <button
                                onClick={(e) => { e.stopPropagation(); setSilencingGroupKey(silencingGroupKey === group.key ? null : group.key); }}
                                className="flex items-center gap-0.5 text-xs rounded px-1.5 py-0.5 transition-colors text-gray-400 hover:text-orange-600 hover:bg-orange-50"
                                title="Silence alarm"
                              >
                                <BellOff className="w-3 h-3" />
                              </button>
                              {silencingGroupKey === group.key && (
                                <div
                                  id="silence-picker"
                                  className="absolute bottom-full right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-2 min-w-[160px]"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <div className="text-xs font-medium text-gray-700 mb-1.5 px-1">Silence for…</div>
                                  {[
                                    { label: '1 day',    days: 1  },
                                    { label: '3 days',   days: 3  },
                                    { label: '1 week',   days: 7  },
                                    { label: '2 weeks',  days: 14 },
                                    { label: '1 month',  days: 30 },
                                  ].map(({ label, days }) => (
                                    <button
                                      key={days}
                                      disabled={silencing}
                                      onClick={() => handleSilenceConfirm(group, days)}
                                      className="w-full text-left text-xs px-2 py-1.5 rounded hover:bg-orange-50 hover:text-orange-700 text-gray-700 disabled:opacity-50"
                                    >
                                      {label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <button
                            onClick={handleAiClick}
                            className={`flex items-center gap-0.5 text-xs rounded px-1.5 py-0.5 transition-colors ${
                              openAiCardId === group.machine_id
                                ? 'bg-cyan-600 text-white'
                                : 'text-cyan-600 hover:text-cyan-800 bg-cyan-50 hover:bg-cyan-100'
                            }`}
                            title="AI health context"
                          >
                            <Sparkles className="w-3 h-3" /> AI
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {alarmGroups.length > visibleGroups && (
                <button
                  onClick={() => setVisibleGroups(v => v + 12)}
                  className="mt-3 w-full py-2 border border-gray-200 rounded-lg text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700 hover:border-gray-300 transition-all"
                >
                  Show more ({alarmGroups.length - visibleGroups} remaining)
                </button>
              )}

              {/* AI health context panel */}
              {openAiCardId && (
                <div className="mt-3 bg-cyan-50 border border-cyan-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-cyan-700 flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> AI Health Context
                    </span>
                    <button
                      onClick={() => setOpenAiCardId(null)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {aiCardLoading && !aiCardResults.has(openAiCardId) ? (
                    <div className="space-y-1.5">
                      {[1, 2, 3].map(i => <div key={i} className="h-4 bg-cyan-100 rounded animate-pulse" />)}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {aiCardResults.get(openAiCardId) || ''}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {/* Alarm detail modal */}
          {selectedGroup && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setSelectedGroup(null)}>
              <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                {/* Modal header */}
                <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{selectedGroup.machine_name}</h3>
                    <p className="text-xs text-gray-400 mt-0.5">{selectedGroup.plant} → {selectedGroup.area_name}</p>
                  </div>
                  <button onClick={() => setSelectedGroup(null)} className="text-gray-400 hover:text-gray-600 transition-colors ml-4 shrink-0">
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {/* Violations table */}
                <div className="px-5 py-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
                    Alarm Events — {selectedGroup.events.length} violation{selectedGroup.events.length !== 1 ? 's' : ''}
                  </p>
                  <div className="space-y-1.5">
                    {selectedGroup.events.map(e => {
                      const velScale = e.unit_code === 2 ? MM_TO_IN : 1;
                      const peakDisp = e.peak_value != null ? e.peak_value * velScale : null;
                      const thrDisp  = e.threshold  != null ? e.threshold  * velScale : null;
                      const overPct = (peakDisp != null && thrDisp != null && thrDisp > 0)
                        ? ((peakDisp - thrDisp) / thrDisp * 100).toFixed(1)
                        : null;
                      const sevColor = e.severity >= 3 ? 'text-red-600' : e.severity >= 2 ? 'text-yellow-600' : 'text-gray-500';
                      const ts2 = e.measurement_at || '';
                      return (
                        <div key={e.id} className="bg-gray-50 rounded-lg px-3 py-2.5 flex flex-col gap-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-gray-800">
                              {e.point_name} — {fmtVelLabel(e.unit_label)}
                              {e.axis_name && <span className="text-gray-400 font-normal"> ({e.axis_name})</span>}
                            </span>
                            {overPct != null && (
                              <span className={`text-xs font-semibold shrink-0 ${sevColor}`}>+{overPct}%</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-500">
                            {peakDisp != null && (
                              <span>Peak: <span className={`font-medium ${sevColor}`}>{peakDisp.toFixed(3)}</span></span>
                            )}
                            {thrDisp != null && (
                              <span>Threshold: <span className="font-medium text-gray-700">{thrDisp.toFixed(3)}</span></span>
                            )}
                            <span className="ml-auto text-gray-400">
                              {fmtEastern(ts2) || e.measurement_at}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                            {e.ei_url && (
                              <a href={e.ei_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:text-blue-700 w-fit">
                                View in EI-Analytic →
                              </a>
                            )}
                            {e.point_name && (
                              <button
                                onClick={() => {
                                  setSelectedGroup(null);
                                  onPointClick?.(selectedGroup.machine_id, selectedGroup.machine_name, e.point_name);
                                }}
                                className="text-xs text-cyan-600 hover:text-cyan-800 font-medium w-fit"
                              >
                                Visit Trend →
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ROC section */}
                <div className="px-5 pb-4 border-t border-gray-100 pt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Rate of Change</p>
                  {rocModalLoading ? (
                    <div className="space-y-1.5">
                      {[1, 2].map(i => <div key={i} className="h-8 bg-gray-100 rounded-lg animate-pulse" />)}
                    </div>
                  ) : groupRoc.length === 0 ? (
                    <p className="text-xs text-gray-400">No rate-of-change data available</p>
                  ) : (
                    <div className="space-y-1.5">
                      {groupRoc.slice(0, 5).map(r => {
                        const pct = r.change_pct ?? 0;
                        const pctColor = pct > 20 ? 'text-red-600' : pct > 5 ? 'text-yellow-600' : pct < -5 ? 'text-green-600' : 'text-gray-600';
                        return (
                          <div key={`${r.axis_id}-${r.unit_label}`} className="bg-gray-50 rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                            <span className="text-xs text-gray-700 truncate">
                              {r.point_name} <span className="text-gray-400">({fmtVelLabel(r.unit_label)})</span>
                            </span>
                            <span className={`text-xs font-semibold shrink-0 ${pctColor}`}>
                              {pct > 0 ? '+' : ''}{pct.toFixed(1)}%
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                <div className="px-5 pb-4 flex gap-2">
                  <button
                    onClick={() => { setSelectedGroup(null); onMachineClick(selectedGroup.machine_id, selectedGroup.machine_name, []); }}
                    className="flex-1 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                  >
                    View Machine
                  </button>
                  {onCreateReport && isAdmin && (
                    <button
                      onClick={() => {
                        const firstEvent = selectedGroup.events[0];
                        setSelectedGroup(null);
                        onCreateReport({ alarmId: String(firstEvent.id), machineName: selectedGroup.machine_name, metric: firstEvent.unit_label });
                      }}
                      className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors flex items-center justify-center gap-1.5"
                    >
                      <Plus className="w-3.5 h-3.5" /> Create Report
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Highest Rate of Change */}
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-gray-900">Highest Rate of Change</h3>
                <p className="text-gray-600">Machines with largest deviations</p>
              </div>
            </div>

            <span className="text-xs text-gray-400">7-day vs prior 45 days</span>
          </div>

          <div className="space-y-2">
            {rocLoading ? (
              [1, 2, 3].map(i => <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />)
            ) : rocError ? (
              <DataUnavailable compact message="Rate-of-change data could not be loaded" />
            ) : machinesWithRoC.length === 0 ? (
              <div className="text-gray-500 py-4 text-center text-sm">No rate-of-change data</div>
            ) : null}
            {!rocLoading && !rocError && machinesWithRoC.map((machine) => (
              <button
                key={machine.id}
                onClick={() => onMachineClick(machine.id, machine.name, machine.points, machine.imageUrl)}
                className="w-full bg-white rounded-lg py-2 px-3 border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all text-left"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${getHealthColor(machine.health)}`} />
                    <span className="text-sm text-gray-900">{machine.name}</span>
                    {machine.plantName && machine.areaName && (
                      <span className="text-xs text-gray-400">{machine.plantName} → {machine.areaName}</span>
                    )}
                  </div>
                  <span className="text-sm text-blue-600 shrink-0 ml-2">
                    {machine.rateOfChange > 0 ? '+' : ''}{machine.rateOfChange}% <span className="text-gray-400 text-xs">{fmtVelLabel(machine.unitLabel)}</span>
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Trend loading overlay */}
    </div>
  );
}